/** `/auth/verify?token=`: exchange a magic-link token for a session, per `docs/AUTH.md`. */
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../auth/AuthContext';
import { Card } from '../components/ui';
import { t } from '../i18n/fr';

type VerifyState = { kind: 'verifying' } | { kind: 'error'; message: string } | { kind: 'done' };

/**
 * Render the magic-link verification screen: reads `token` from the query
 * string, calls `POST auth/verify/`, and lands the volunteer on `/` once
 * signed in (the route guards then send them on to onboarding or pending
 * validation as needed).
 *
 * @returns The page element.
 */
export function VerifyPage() {
  const [searchParams] = useSearchParams();
  const { verifyMagicLink } = useAuth();
  const token = searchParams.get('token');
  const [state, setState] = useState<VerifyState>({ kind: 'verifying' });
  // A magic-link token is single-use, so it must be posted exactly once.
  // React's development StrictMode runs this effect twice; keeping the
  // in-flight request here lets the second run await the same response
  // instead of spending an already-consumed token.
  const pending = useRef<{ token: string; request: Promise<unknown> } | null>(null);

  useEffect(() => {
    if (!token) {
      setState({ kind: 'error', message: t('auth.verify.missingToken') });
      return;
    }
    if (pending.current?.token !== token) {
      pending.current = { token, request: verifyMagicLink(token) };
    }
    let cancelled = false;
    pending.current.request
      .then(() => {
        if (!cancelled) {
          setState({ kind: 'done' });
        }
      })
      .catch(() => {
        if (!cancelled) {
          setState({ kind: 'error', message: t('auth.verify.error') });
        }
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- verifyMagicLink is stable, only token should re-trigger
  }, [token]);

  if (state.kind === 'done') {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="auth-screen">
      <Card>
        {state.kind === 'verifying' ? (
          <p role="status">{t('auth.verify.verifying')}</p>
        ) : (
          <>
            <p className="ui-field__error" role="alert">
              {state.message}
            </p>
            <Link className="ui-btn ui-btn--secondary" to="/login">
              {t('auth.verify.backToLogin')}
            </Link>
          </>
        )}
      </Card>
    </div>
  );
}
